import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import EditProfileForm from '../components/forms/EditProfileForm';
import ChangePasswordForm from '../components/forms/ChangePasswordForm';

const SettingsPage = () => {
  const { currentUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [animatingOut, setAnimatingOut] = useState(false);
  const [visibleContent, setVisibleContent] = useState('profile');

  // Handle tab switching with animation
  useEffect(() => {
    if (activeTab !== visibleContent) {
      setAnimatingOut(true);
      const timer = setTimeout(() => {
        setVisibleContent(activeTab);
        setAnimatingOut(false);
      }, 300); // match this to CSS transition time
      return () => clearTimeout(timer);
    }
  }, [activeTab, visibleContent]);

  if (!isAuthenticated) {
    return (
      <div className="container mt-5 pt-5">
        <div className="alert alert-warning">
          <h4>Authentication Required</h4>
          <p>Please log in to view this page.</p>
          <button className="button" onClick={() => navigate('/login')}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const handleTabChange = (tab) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  };

  return (
    <section id="settings" className="pb-5" style={{ paddingTop: '120px', minHeight: '100vh' }}>
      <div className="container">
        {/* Page Title with gradient effect */}
        <div className="row mb-4">
          <div className="col-12">
            <h1 className="text-white">
              <span className="gradient-text">Settings</span>
            </h1>
            <div className="gradient-divider mb-4"></div>
          </div>
        </div>
        
        <div className="row">
          {/* Settings Sidebar */}
          <div className="col-md-3 mb-4">
            <div className="settings-sidebar card bg_grey border-0 shadow">
              <div className="card-header bg-transparent border-0">
                <h4 className="text-white mb-0 p-2">Menu</h4>
              </div>
              <div className="list-group list-group-flush settings-menu">
                <button 
                  className={`settings-menu-item list-group-item list-group-item-action bg-dark text-white ${activeTab === 'profile' ? 'active' : ''}`} 
                  onClick={() => handleTabChange('profile')}
                >
                  <i className="fa fa-user me-3"></i> Profile Settings
                </button>
                <button 
                  className={`settings-menu-item list-group-item list-group-item-action bg-dark text-white ${activeTab === 'password' ? 'active' : ''}`}
                  onClick={() => handleTabChange('password')}
                >
                  <i className="fa fa-lock me-3"></i> Change Password
                </button>
                <button 
                  className={`settings-menu-item list-group-item list-group-item-action bg-dark text-white ${activeTab === 'notifications' ? 'active' : ''}`}
                  onClick={() => handleTabChange('notifications')}
                >
                  <i className="fa fa-bell me-3"></i> Notification Settings
                </button>
                <button 
                  className={`settings-menu-item list-group-item list-group-item-action bg-dark text-white ${activeTab === 'privacy' ? 'active' : ''}`}
                  onClick={() => handleTabChange('privacy')}
                >
                  <i className="fa fa-shield me-3"></i> Privacy Settings
                </button>
              </div>
            </div>
          </div>
          
          {/* Settings Content */}
          <div className="col-md-9">
            <div className="card bg_grey border-0 shadow">
              <div className="card-body p-4">
                <div className={`tab-content ${animatingOut ? 'fade-out' : 'fade-in'}`}>
                  {visibleContent === 'profile' && (
                    <div className="tab-pane">
                      <h3 className="text-white mb-4">
                        <span className="gradient-text">Profile Settings</span>
                      </h3>
                      <div className="gradient-divider mb-4"></div>
                      {currentUser && <EditProfileForm user={currentUser} updateUser={() => {}} onCancel={() => {}} />}
                    </div>
                  )}
                  
                  {visibleContent === 'password' && (
                    <div className="tab-pane">
                      <h3 className="text-white mb-4">
                        <span className="gradient-text">Change Password</span>
                      </h3>
                      <div className="gradient-divider mb-4"></div>
                      <ChangePasswordForm />
                    </div>
                  )}
                  
                  {visibleContent === 'notifications' && (
                    <div className="tab-pane">
                      <h3 className="text-white mb-4">
                        <span className="gradient-text">Notification Settings</span>
                      </h3>
                      <div className="gradient-divider mb-4"></div>
                      
                      <div className="settings-card mb-4 p-3">
                        <h5 className="text-white mb-3">Email Notifications</h5>
                        <div className="form-check form-switch custom-switch mb-3">
                          <input className="form-check-input" type="checkbox" id="emailNotif" defaultChecked />
                          <label className="form-check-label text-white ms-2" htmlFor="emailNotif">
                            Email Notifications
                          </label>
                          <p className="text-muted mt-1 mb-0 settings-description">Receive important updates and announcements via email</p>
                        </div>
                      </div>
                      
                      <div className="settings-card mb-4 p-3">
                        <h5 className="text-white mb-3">App Notifications</h5>
                        <div className="form-check form-switch custom-switch mb-3">
                          <input className="form-check-input" type="checkbox" id="pushNotif" defaultChecked />
                          <label className="form-check-label text-white ms-2" htmlFor="pushNotif">
                            Push Notifications
                          </label>
                          <p className="text-muted mt-1 mb-0 settings-description">Get real-time alerts on your device</p>
                        </div>
                      </div>
                      
                      <div className="settings-card mb-4 p-3">
                        <h5 className="text-white mb-3">Event Notifications</h5>
                        <div className="form-check form-switch custom-switch mb-3">
                          <input className="form-check-input" type="checkbox" id="eventNotif" defaultChecked />
                          <label className="form-check-label text-white ms-2" htmlFor="eventNotif">
                            Event Reminders
                          </label>
                          <p className="text-muted mt-1 mb-0 settings-description">Get reminded about upcoming events you're attending</p>
                        </div>
                        
                        <div className="form-check form-switch custom-switch mb-3">
                          <input className="form-check-input" type="checkbox" id="momentNotif" defaultChecked />
                          <label className="form-check-label text-white ms-2" htmlFor="momentNotif">
                            Moment Notifications
                          </label>
                          <p className="text-muted mt-1 mb-0 settings-description">Get notified when new moments are available for your events</p>
                        </div>
                      </div>
                      
                      <button className="button mt-3">
                        <i className="fa fa-save me-2"></i> Save Settings
                      </button>
                    </div>
                  )}
                  
                  {visibleContent === 'privacy' && (
                    <div className="tab-pane">
                      <h3 className="text-white mb-4">
                        <span className="gradient-text">Privacy Settings</span>
                      </h3>
                      <div className="gradient-divider mb-4"></div>
                      
                      <div className="settings-card mb-4 p-3">
                        <h5 className="text-white mb-3">Profile Visibility</h5>
                        <div className="form-check form-switch custom-switch mb-3">
                          <input className="form-check-input" type="checkbox" id="profileVisibility" defaultChecked />
                          <label className="form-check-label text-white ms-2" htmlFor="profileVisibility">
                            Public Profile
                          </label>
                          <p className="text-muted mt-1 mb-0 settings-description">Allow others to view your profile</p>
                        </div>
                      </div>
                      
                      <div className="settings-card mb-4 p-3">
                        <h5 className="text-white mb-3">Location Services</h5>
                        <div className="form-check form-switch custom-switch mb-3">
                          <input className="form-check-input" type="checkbox" id="locationSharing" />
                          <label className="form-check-label text-white ms-2" htmlFor="locationSharing">
                            Share Location
                          </label>
                          <p className="text-muted mt-1 mb-0 settings-description">Allow the app to use your location for nearby events</p>
                        </div>
                      </div>
                      
                      <div className="settings-card mb-4 p-3">
                        <h5 className="text-white mb-3">Activity Visibility</h5>
                        <div className="form-check form-switch custom-switch mb-3">
                          <input className="form-check-input" type="checkbox" id="activityVisibility" defaultChecked />
                          <label className="form-check-label text-white ms-2" htmlFor="activityVisibility">
                            Show my event activity
                          </label>
                          <p className="text-muted mt-1 mb-0 settings-description">Allow others to see events you've attended</p>
                        </div>
                      </div>
                      
                      <button className="button mt-3">
                        <i className="fa fa-save me-2"></i> Save Settings
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Add custom styles */}
      <style jsx="true">{`
        .gradient-text {
          background: linear-gradient(to right, #ff3636, #c93e67);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          display: inline-block;
        }
        
        .gradient-divider {
          height: 3px;
          background: linear-gradient(to right, #ff3636, #c93e67);
          border-radius: 3px;
          width: 100px;
          margin-top: -5px;
        }
        
        .settings-menu-item {
          border: none;
          border-radius: 5px;
          margin-bottom: 8px;
          transition: all 0.3s ease;
        }
        
        .settings-menu-item:hover {
          background: linear-gradient(to right, rgba(255, 54, 54, 0.15), rgba(201, 62, 103, 0.15)) !important;
          transform: translateX(5px);
        }
        
        .settings-menu-item.active {
          background: linear-gradient(to right, rgba(255, 54, 54, 0.3), rgba(201, 62, 103, 0.3)) !important;
          border-left: 3px solid #ff3636;
        }
        
        .settings-description {
          font-size: 0.85rem;
          opacity: 0.7;
        }
        
        .settings-card {
          background-color: rgba(33, 37, 41, 0.5);
          border-radius: 8px;
          transition: all 0.3s ease;
        }
        
        .settings-card:hover {
          background-color: rgba(33, 37, 41, 0.8);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
        }
        
        .form-check-input:checked {
          background-color: #ff3636;
          border-color: #ff3636;
        }
        
        .custom-switch .form-check-input {
          width: 3em;
          height: 1.5em;
        }
        
        .button {
          background: linear-gradient(to right, #ff3636, #c93e67);
          border: none;
          transition: all 0.3s ease;
        }
        
        .button:hover {
          box-shadow: 0 5px 15px rgba(255, 54, 54, 0.4);
          transform: translateY(-2px);
        }
        
        /* Animation styles */
        .tab-content {
          transition: opacity 0.3s ease;
          position: relative;
          min-height: 200px;
        }
        
        .fade-in {
          opacity: 1;
        }
        
        .fade-out {
          opacity: 0;
        }
        
        @media (max-width: 768px) {
          .settings-menu-item:hover {
            transform: none;
          }
          
          .settings-card {
            padding: 15px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default SettingsPage;
